/**
 * FocusCategorySelector
 * AI 분석 시 중점 분석 카테고리를 선택하는 컴포넌트.
 *
 * - desktop: 드롭다운 멀티셀렉트 (선택 항목 태그로 표시)
 * - phone / fold: 칩 목록 + "+ 추가" 바텀시트
 *
 * 사용 예시:
 * <FocusCategorySelector
 *   selected={focusCategories}
 *   onChange={setFocusCategories}
 *   disabled={!enableAIAnalysis || hasReport}
 *   layout="desktop"
 *   companyId={companyId}
 *   siteId={siteId}
 * />
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SYSTEM_HAZARD_CATEGORIES } from '@safetyhub/shared';
import type { HazardCategory } from '@safetyhub/shared';
import { Button } from '@safetyhub/ui';
import { hazardCategoriesApi } from '../../../lib/organizations';
import { useBottomSheetKeyboard } from '../../../hooks/useBottomSheetKeyboard';
import { XIcon } from '../../icons';

export interface FocusCategorySelectorProps {
  selected: string[];
  onChange: (categories: string[]) => void;
  disabled?: boolean;
  layout: 'desktop' | 'phone' | 'fold';
  /** 조직 ID: 있으면 API에서 카테고리 로드, 없으면 하드코딩 사용 */
  companyId?: string;
  /** 현장 ID: 업종별 기본 카테고리 정렬에 사용 */
  siteId?: string;
}

// ─── 공통 유틸 ────────────────────────────────────────────────────────────────

function toggle(current: string[], code: string): string[] {
  return current.includes(code)
    ? current.filter((c) => c !== code)
    : [...current, code];
}

/** API 카테고리 또는 하드코딩 fallback에서 label 조회 */
function getLabelByCode(code: string, categories: HazardCategory[]): string {
  return categories.find((c) => c.code === code)?.label ?? code;
}

/** useQuery로 카테고리 목록 조회, companyId 없으면 시스템 하드코딩 반환 */
function useHazardCategories(companyId?: string, siteId?: string) {
  const fallback: HazardCategory[] = SYSTEM_HAZARD_CATEGORIES.map((c) => ({
    code: c.code,
    label: c.label,
    isSystem: true,
  }));

  const { data } = useQuery({
    queryKey: ['hazardCategories', companyId, siteId],
    queryFn: () => hazardCategoriesApi.getAll(companyId!, siteId),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  });

  return companyId ? (data ?? fallback) : fallback;
}

// ─── 공통 카테고리 생성 헬퍼 ────────────────────────────────────────────────

/**
 * API를 통해 커스텀 카테고리를 생성하고 쿼리 캐시를 무효화합니다.
 * DesktopSelector와 MobileSelector 양쪽에서 공유합니다.
 *
 * @param companyId 조직 ID
 * @param siteId 현장 ID (queryKey 동기화에 사용)
 * @param label 생성할 카테고리 이름
 * @param queryClient TanStack Query 클라이언트
 * @param beforeChange 캐시 무효화 후, onChange 호출 전 실행할 선택적 콜백
 * @returns 생성된 카테고리 코드 (실패 시 label을 fallback으로 반환)
 */
async function createCategoryAndInvalidate(
  companyId: string,
  siteId: string | undefined,
  label: string,
  queryClient: ReturnType<typeof useQueryClient>,
  beforeChange?: () => void,
): Promise<string> {
  try {
    const created = await hazardCategoriesApi.create(companyId, label);
    await queryClient.invalidateQueries({ queryKey: ['hazardCategories', companyId, siteId] });
    beforeChange?.();
    return created.code;
  } catch {
    // 생성 실패 시 label을 code로 fallback 사용
    beforeChange?.();
    return label;
  }
}

// ─── Desktop: 드롭다운 멀티셀렉트 ────────────────────────────────────────────

function DesktopSelector({
  selected,
  onChange,
  disabled,
  companyId,
  siteId,
}: Omit<FocusCategorySelectorProps, 'layout'>) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownLayout, setDropdownLayout] = useState<{
    style: React.CSSProperties;
    listMaxHeight: number;
  }>({ style: {}, listMaxHeight: 280 });
  const queryClient = useQueryClient();

  const categories = useHazardCategories(companyId, siteId);

  // 드롭다운 열릴 때: 위치 계산 (항상 아래로 열기)
  useEffect(() => {
    if (!open) return;

    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const GAP = 4;
      const SEARCH_BAR_HEIGHT = 48; // 검색창 높이 예약
      const spaceBelow = window.innerHeight - rect.bottom - GAP;
      const availableHeight = Math.min(Math.max(spaceBelow, 80), 280);

      setDropdownLayout({
        style: {
          position: 'fixed',
          top: rect.bottom + GAP,
          left: rect.left,
          zIndex: 9999,
        },
        listMaxHeight: Math.max(availableHeight - SEARCH_BAR_HEIGHT, 40),
      });
    }

    return undefined;
  }, [open]);

  // 외부 클릭 시 닫기
  const skipCloseRef = useRef(false);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (skipCloseRef.current) {
        skipCloseRef.current = false;
        return;
      }
      const target = e.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredCategories = categories.filter(({ label }) =>
    !searchQuery || label.includes(searchQuery)
  );
  const trimmedSearch = searchQuery.trim();
  const canCreate = !!companyId &&
    !!trimmedSearch &&
    filteredCategories.length === 0 &&
    !selected.includes(trimmedSearch);
  const placeholder = selected.length === 0 ? '전체 위험 분석' : null;

  const handleCreate = async () => {
    if (!companyId || !trimmedSearch || isCreating) return;
    setIsCreating(true);
    try {
      const code = await createCategoryAndInvalidate(
        companyId,
        siteId,
        trimmedSearch,
        queryClient,
        () => { skipCloseRef.current = true; },
      );
      onChange([...selected, code]);
      setSearchQuery('');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {/* 트리거 버튼 */}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => {
            if (!v) { setSearchQuery(''); setTimeout(() => { searchInputRef.current?.focus(); searchInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 50); }
            return !v;
          });
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={[
          'flex items-center gap-1 min-w-[120px] max-w-[280px] h-8 px-2 rounded-control border text-sm',
          'focus:outline-none focus:ring-2 focus:ring-brand-500',
          disabled
            ? 'bg-surface-soft border-line text-ink-400 cursor-not-allowed'
            : 'bg-white border-ink-300 text-ink-700 hover:border-ink-400 cursor-pointer',
        ].join(' ')}
      >
        <span className="flex-1 flex items-center gap-1 overflow-hidden">
          {placeholder ? (
            <span className="text-ink-400 truncate">{placeholder}</span>
          ) : (
            <>
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-brand-100 text-brand-700 rounded text-xs leading-none whitespace-nowrap">
                {getLabelByCode(selected[0], categories)}
                {!disabled && (
                  <button
                    type="button"
                    aria-label={`${getLabelByCode(selected[0], categories)} 제거`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      skipCloseRef.current = true;
                      onChange(selected.filter((c) => c !== selected[0]));
                    }}
                    className="ml-0.5 hover:text-brand-900"
                  >
                    <XIcon className="h-3 w-3" />
                  </button>
                )}
              </span>
              {selected.length > 1 && (
                <span className="text-xs text-brand-600 font-medium whitespace-nowrap">+{selected.length - 1}</span>
              )}
            </>
          )}
        </span>
        <svg
          className={`shrink-0 w-3.5 h-3.5 text-ink-400 transition-transform ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {/* 드롭다운 목록 (portal로 body에 렌더링하여 overflow 잘림 방지) */}
      {open && createPortal(
        // Card 갈래 아님: body portal 드롭다운 패널(위치를 inline style로 잡아
        // absolute/fixed 클래스가 없어 규칙이 못 거른다).
        // eslint-disable-next-line local/no-self-painted-card
        <div
          ref={dropdownRef}
          style={dropdownLayout.style}
          className="min-w-[200px] bg-white border border-line rounded-card shadow-card"
          onWheel={(e) => e.stopPropagation()}
        >
          {/* 검색 입력 + 결과 없을 때 추가 버튼 */}
          <div className="p-2 border-b border-line-soft flex items-center gap-1.5">
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onMouseDown={(e) => { e.stopPropagation(); skipCloseRef.current = true; }}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (canCreate) {
                    handleCreate();
                  } else if (trimmedSearch && !selected.includes(trimmedSearch) && filteredCategories.length === 0) {
                    // companyId 없을 때 fallback: 바로 selected에 추가
                    skipCloseRef.current = true;
                    onChange([...selected, trimmedSearch]);
                    setSearchQuery('');
                  }
                }
              }}
              placeholder="검색..."
              className="flex-1 px-2 py-1 text-sm border border-ink-300 rounded-control focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            {canCreate && (
              <Button
                type="button"
                variant="primary"
                size="xs"
                className="flex-shrink-0 whitespace-nowrap"
                disabled={isCreating}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCreate();
                }}
              >
                {isCreating ? '추가 중...' : '추가'}
              </Button>
            )}
            {/* companyId 없을 때 fallback: 바로 추가 */}
            {!companyId && trimmedSearch && filteredCategories.length === 0 && !selected.includes(trimmedSearch) && (
              <Button
                type="button"
                variant="primary"
                size="xs"
                className="flex-shrink-0 whitespace-nowrap"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  skipCloseRef.current = true;
                  onChange([...selected, trimmedSearch]);
                  setSearchQuery('');
                }}
              >
                추가
              </Button>
            )}
          </div>
        <ul
          role="listbox"
          aria-multiselectable="true"
          aria-label="위험 카테고리 선택"
          className="overflow-y-auto py-1"
          style={{ maxHeight: dropdownLayout.listMaxHeight }}
        >
          {filteredCategories.map(({ code, label }) => {
            const isSelected = selected.includes(code);
            return (
              <li
                key={code}
                role="option"
                aria-selected={isSelected}
                onMouseDown={(e) => {
                  e.preventDefault();
                  skipCloseRef.current = true;
                  onChange(toggle(selected, code));
                }}
                className={[
                  'flex items-center gap-2 px-3 py-2 text-sm cursor-pointer select-none',
                  isSelected ? 'bg-brand-50 text-brand-700' : 'text-ink-800 hover:bg-surface-hover',
                ].join(' ')}
              >
                <span
                  className={[
                    'w-4 h-4 rounded border flex items-center justify-center shrink-0',
                    isSelected ? 'bg-brand-600 border-brand-600' : 'border-ink-300',
                  ].join(' ')}
                  aria-hidden="true"
                >
                  {isSelected && (
                    <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 12" fill="currentColor">
                      <path d="M10 3L5 8.5 2 5.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                {label}
              </li>
            );
          })}
          {/* 검색 결과 없을 때 안내 */}
          {filteredCategories.length === 0 && trimmedSearch && (
            <li className="px-3 py-3 text-sm text-ink-500 text-center">
              "{trimmedSearch}" — 엔터 또는 추가 버튼으로 등록
            </li>
          )}
        </ul>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Mobile (phone / fold): 칩 + 바텀시트 ────────────────────────────────────

function MobileSelector({
  selected,
  onChange,
  disabled,
  companyId,
  siteId,
}: Omit<FocusCategorySelectorProps, 'layout'>) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const categories = useHazardCategories(companyId, siteId);
  const sheetStyle = useBottomSheetKeyboard(sheetOpen);

  // 바텀시트 열릴 때 배경 스크롤 잠금
  useEffect(() => {
    if (sheetOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [sheetOpen]);

  const trimmedSearch = searchQuery.trim();
  const filteredCategories = categories.filter(({ label }) =>
    !searchQuery || label.includes(searchQuery)
  );
  const canCreate = !!companyId &&
    !!trimmedSearch &&
    filteredCategories.length === 0 &&
    !selected.includes(trimmedSearch);

  const handleCreate = async () => {
    if (!companyId || !trimmedSearch || isCreating) return;
    setIsCreating(true);
    try {
      const code = await createCategoryAndInvalidate(
        companyId,
        siteId,
        trimmedSearch,
        queryClient,
      );
      onChange([...selected, code]);
      setSearchQuery('');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      {/* 축약 표시 (첫 번째 카테고리 + N) — 탭하면 바텀시트 */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => { if (!disabled) { setSearchQuery(''); setSheetOpen(true); } }}
        className={[
          'inline-flex items-center gap-1 px-2 py-1 rounded-control text-xs whitespace-nowrap',
          disabled
            ? 'bg-surface-soft text-ink-400 cursor-not-allowed'
            : selected.length > 0
              ? 'bg-brand-50 text-brand-700 active:bg-brand-100'
              : 'bg-surface-soft text-ink-400 active:bg-ink-100',
        ].join(' ')}
      >
        {selected.length === 0 ? (
          <span>전체</span>
        ) : (
          <>
            <span>{getLabelByCode(selected[0], categories)}</span>
            {selected.length > 1 && (
              <span className="font-medium">+{selected.length - 1}</span>
            )}
          </>
        )}
        <svg className="w-3 h-3 text-ink-400" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>

      {/* 바텀시트 (createPortal로 body에 직접 렌더링) */}
      {sheetOpen && createPortal(
        <>
          {/* 배경 오버레이 */}
          <div
            className="fixed inset-0 z-[9998] bg-black/30"
            onClick={() => setSheetOpen(false)}
            aria-hidden="true"
            style={{ touchAction: 'none' }}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="분석 카테고리 선택"
            className="fixed left-0 right-0 z-[9999] bg-white rounded-t-2xl shadow-xl flex flex-col"
            style={{ bottom: sheetStyle.bottom ?? 0, maxHeight: sheetStyle.maxHeight ?? '70vh', paddingBottom: 'var(--bottom-nav-height, 0px)' }}
          >
            {/* 핸들 */}
            <div className="flex justify-center pt-2 pb-0.5">
              <div className="w-10 h-1 bg-ink-300 rounded-full" aria-hidden="true" />
            </div>

            {/* 헤더 */}
            <div className="flex items-center justify-between px-4 py-1.5 border-b border-line-soft">
              <h2 className="text-base font-semibold text-ink-900">분석 카테고리</h2>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="p-1 text-ink-500 hover:text-ink-700 rounded"
                aria-label="닫기"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* 검색 + 결과 없을 때 추가 버튼 */}
            <div className="px-4 pt-2 pb-1 flex items-center gap-2">
              <input
                ref={searchInputRef}
                type="text"
                enterKeyHint="done"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (canCreate) {
                      handleCreate();
                    } else if (trimmedSearch && filteredCategories.length === 0 && !selected.includes(trimmedSearch)) {
                      onChange([...selected, trimmedSearch]);
                      setSearchQuery('');
                    } else {
                      (e.target as HTMLInputElement).blur();
                    }
                  }
                }}
                placeholder="카테고리 검색..."
                className="flex-1 px-3 py-2 text-sm border border-ink-300 rounded-control focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
              {(canCreate || (!companyId && trimmedSearch && filteredCategories.length === 0 && !selected.includes(trimmedSearch))) && (
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  className="flex-shrink-0 whitespace-nowrap"
                  disabled={isCreating}
                  onClick={() => {
                    if (canCreate) {
                      handleCreate();
                    } else {
                      onChange([...selected, trimmedSearch]);
                      setSearchQuery('');
                    }
                  }}
                >
                  {isCreating ? '추가 중...' : '추가'}
                </Button>
              )}
            </div>

            {/* 안내 문구 */}
            <p className="px-4 pt-2 pb-1 text-xs text-ink-500">
              미선택 시 전체 위험 유형을 분석합니다.
            </p>

            {/* 카테고리 목록 */}
            <ul
              role="listbox"
              aria-multiselectable="true"
              className="flex-1 overflow-y-auto px-4 pb-4"
              style={{ overscrollBehavior: 'contain' }}
            >
              {filteredCategories.map(({ code, label }) => {
                const isSelected = selected.includes(code);
                return (
                  <li key={code} role="option" aria-selected={isSelected}>
                    <button
                      type="button"
                      onClick={() => onChange(toggle(selected, code))}
                      className={[
                        'w-full flex items-center gap-3 py-2.5 border-b border-line-soft text-sm text-left',
                        isSelected ? 'text-brand-700' : 'text-ink-800',
                      ].join(' ')}
                    >
                      <span
                        className={[
                          'w-5 h-5 rounded border flex items-center justify-center shrink-0',
                          isSelected ? 'bg-brand-600 border-brand-600' : 'border-ink-300',
                        ].join(' ')}
                        aria-hidden="true"
                      >
                        {isSelected && (
                          <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="currentColor">
                            <path d="M10 3L5 8.5 2 5.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </span>
                      {label}
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* 하단 확인 버튼 */}
            <div className="px-4 pb-6 pt-2 border-t border-line-soft">
              {/* 모바일 시트 하단 CTA — lg(44px)가 터치 타깃에 맞는다. */}
              <Button variant="primary" size="lg" onClick={() => setSheetOpen(false)} className="w-full">
                확인
                {selected.length > 0 && ` (${selected.length}개 선택됨)`}
              </Button>
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  );
}

// ─── 공개 컴포넌트 ─────────────────────────────────────────────────────────────

export function FocusCategorySelector({ selected, onChange, disabled = false, layout, companyId, siteId }: FocusCategorySelectorProps) {
  if (layout === 'desktop') {
    return <DesktopSelector selected={selected} onChange={onChange} disabled={disabled} companyId={companyId} siteId={siteId} />;
  }
  return <MobileSelector selected={selected} onChange={onChange} disabled={disabled} companyId={companyId} siteId={siteId} />;
}
